import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { ApiConsumer } from '../../../service/api-consumer';

import { ShellVersionsResponse } from '../../../interface/response/ShellVersionsResponse';
import { UpdateShellRequest } from '../../../interface/request/UpdateShellRequest';
import { DeviceStatusResponse } from '../../../interface/response/device-status/DeviceStatusResponse';
import { DeviceResponse } from '../../../interface/response/device/DeviceResponse';
import { GraphDeviceLogs } from '../../../interface/response/device-logs/DeviceLogsResponse';
import { DeviceLogsRequest } from '../../../interface/request/DeviceLogsRequest';

import { transformLogs } from '../../../util/health-monitor/graph/graph';

interface DeviceDetails {
  deviceDetails: DeviceResponse | null;
  shellVersions: ShellVersionsResponse[] | null;
  deviceStatus: DeviceStatusResponse | null;
  deviceLogs: GraphDeviceLogs[] | null;
  remoteControlId: string | null;
  isGraphActive: boolean;
  isLoadingSomeOfDeviceDetails: boolean;
  isLoadingGraphData: boolean;
  isLiveData: boolean;
  errorMessage: string | null;
}

const initialState: DeviceDetails = {
  deviceDetails: null,
  shellVersions: null,
  deviceStatus: null,
  deviceLogs: null,
  remoteControlId: null,
  isGraphActive: false,
  isLoadingSomeOfDeviceDetails: false,
  isLoadingGraphData: false,
  isLiveData: true,
  errorMessage: null,
};

export const getDeviceDetails = createAsyncThunk('/deviceInfo', async (deviceId: string) => {
  const response = await ApiConsumer.getDeviceDetails(deviceId);

  return response;
});

export const getShellVersions = createAsyncThunk('/shell/versions', async (deviceUserId: string) => {
  const response = await ApiConsumer.getShellVersions(deviceUserId);

  return response;
});

export const setShellVersions = createAsyncThunk(
  '/shell/setVersion',
  async (updateShellRequest: UpdateShellRequest) => {
    const response = await ApiConsumer.setShellVersions(updateShellRequest);

    return response;
  },
);

export const restartShell = createAsyncThunk('/shell/restart', async (deviceUserId: string) => {
  const response = await ApiConsumer.restartShell(deviceUserId);

  return response;
});

export const getDeviceStatus = createAsyncThunk('/status', async (deviceUserId: string | undefined) => {
  const response = await ApiConsumer.getDeviceStatus(deviceUserId);

  return response;
});

export const getDeviceLogs = createAsyncThunk('/deviceLogs', async (deviceLogsRequest: DeviceLogsRequest) => {
  const response = await ApiConsumer.getDeviceLogs(deviceLogsRequest);

  return response;
});

export const getRemoteAccess = createAsyncThunk('/remoteAccess', async (remoteControlId: string | null) => {
  const response = await ApiConsumer.getRemoteAccess(remoteControlId);

  return response;
});

const deviceDetailsSlice = createSlice({
  name: 'deviceDetails',
  initialState,
  reducers: {
    toggleIsLiveData: (state) => {
      state.isLiveData = !state.isLiveData;
    },
    setIsLiveData: (state, { payload }) => {
      state.isLiveData = payload;
    },
    resetDeviceDetails: (state) => {
      state.errorMessage = null;
      state.deviceDetails = null;
      state.remoteControlId = null;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getDeviceDetails.fulfilled, (state, { payload }) => {
      state.deviceDetails = payload;
      state.remoteControlId = payload?.additionalInformation?.remoteControlId;
    });

    builder.addCase(getDeviceDetails.pending, (state) => {});

    builder.addCase(getDeviceDetails.rejected, (state) => {
      state.errorMessage = 'failedToLoad';
    });

    builder.addCase(getShellVersions.fulfilled, (state, { payload }) => {
      state.shellVersions = payload;
      state.isLoadingSomeOfDeviceDetails = false;
    });

    builder.addCase(getShellVersions.pending, (state) => {
      state.isLoadingSomeOfDeviceDetails = true;
    });

    builder.addCase(getShellVersions.rejected, (state) => {
      state.isLoadingSomeOfDeviceDetails = false;
    });

    builder.addCase(setShellVersions.fulfilled, (state, { payload }) => {
      state.isLoadingSomeOfDeviceDetails = false;
    });

    builder.addCase(setShellVersions.pending, (state) => {
      state.isLoadingSomeOfDeviceDetails = true;
    });

    builder.addCase(setShellVersions.rejected, (state) => {
      state.isLoadingSomeOfDeviceDetails = false;
    });

    builder.addCase(restartShell.fulfilled, (state, { payload }) => {
      state.isLoadingSomeOfDeviceDetails = false;
    });

    builder.addCase(restartShell.pending, (state) => {
      state.isLoadingSomeOfDeviceDetails = true;
    });

    builder.addCase(restartShell.rejected, (state) => {
      state.isLoadingSomeOfDeviceDetails = false;
    });

    builder.addCase(getDeviceStatus.fulfilled, (state, { payload }) => {
      state.deviceStatus = payload;
    });

    builder.addCase(getDeviceStatus.pending, (state) => {});

    builder.addCase(getDeviceStatus.rejected, (state) => {});

    builder.addCase(getDeviceLogs.fulfilled, (state, { payload }) => {
      state.deviceLogs = transformLogs(payload);
      state.isLoadingGraphData = false;
    });

    builder.addCase(getDeviceLogs.pending, (state) => {
      state.isLoadingGraphData = true;
    });

    builder.addCase(getDeviceLogs.rejected, (state) => {
      state.isLoadingGraphData = false;
    });

    builder.addCase(getRemoteAccess.fulfilled, (state, { payload }) => {
      state.isLoadingSomeOfDeviceDetails = false;
      window.open(payload, '_blank');
    });

    builder.addCase(getRemoteAccess.pending, (state) => {
      state.isLoadingSomeOfDeviceDetails = true;
    });

    builder.addCase(getRemoteAccess.rejected, (state) => {
      state.isLoadingSomeOfDeviceDetails = false;
    });
  },
});

export const { toggleIsLiveData, setIsLiveData, resetDeviceDetails } = deviceDetailsSlice.actions;

export default deviceDetailsSlice.reducer;
